import { computed, DestroyRef, Directive, inject, Injector, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  StatusChangeEvent,
  TouchedChangeEvent,
} from '@angular/forms';
import { distinctUntilChanged, filter, startWith } from 'rxjs';
import { generateId } from 'shared';

@Directive({
  selector: '[dsBaseControl]',
})
export abstract class BaseControlDirective<Tvalue> implements ControlValueAccessor, OnInit {
  public id = input<string | undefined>();
  public label = input<string | undefined>();
  public required = input(false);

  private readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);

  protected control!: FormControl;
  public onChange!: (value: Tvalue) => void; // view to model update
  public onTouched!: () => void; // register interaction to model
  private _disabled = false;

  hasError = signal(false);

  public controlId = computed(() => this.id() || generateId('')); // guarantee an id

  protected isDisabled() {
    return this._disabled;
  }

  public ngOnInit(): void {
    this.setFormControl();

    this.control.events
      .pipe(
        startWith({ status: this.control.status, source: this.control }),
        filter((event) => event instanceof StatusChangeEvent || event instanceof TouchedChangeEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event instanceof TouchedChangeEvent && event.touched && !this.control.value?.length) {
          setTimeout(() => this.hasError.set(this.required()));
        }

        if (event instanceof StatusChangeEvent) {
          setTimeout(() => this.hasError.set(event.status === 'INVALID'));
        }
      });

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(this.control.value), distinctUntilChanged())
      .subscribe(() => {
        this.doActionOnControlChanges();
        this.control?.markAsUntouched(); // reset touched to not show errors when start typing again
      });
  }

  /** available to be able to override the actions taken on value changes */
  public doActionOnControlChanges() {}

  private setFormControl() {
    try {
      const control = this.injector.get(NgControl);

      switch (control.constructor) {
        case FormControlName:
          this.control = this.injector.get(FormGroupDirective).getControl(control as FormControlName);
          break;

        default:
          this.control = (control as FormControlDirective).form as FormControl;
          break;
      }
    } catch {
      this.control = new FormControl();
    }
  }

  public controlInvalid(): boolean {
    return !!this.control && this.control.invalid && (this.control.touched || this.control.dirty);
  }

  // model to view update
  public writeValue(value: Tvalue): void {
    if (this.control.value === value) return;
    this.control.setValue(value, { emitEvent: false, emitModelToViewChange: false });
  }

  public registerOnChange(fn: (value: Tvalue | null) => Tvalue): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }
}
